/**
 * Recensioni API Queries
 * Centralized data fetching for Recensioni feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Recensione, RecensioneInsert, RecensioneUpdate,
  QueryOptions 
} from '@/types';

const TABLE = 'recensioni';

// ============================================================
// GET Queries
// ============================================================

export async function getRecensioni(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*, strutture(nome), prenotazioni(ospite_nome, struttura_nome)', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('data', { ascending: false });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as (Recensione & { 
      strutture: { nome: string } | null;
      prenotazioni: { ospite_nome: string; struttura_nome: string } | null 
    })[] | null,
    error,
    count,
  };
}

export async function getRecensioneById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, strutture(*), prenotazioni(*)')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as (Recensione & { 
      strutture: unknown;
      prenotazioni: unknown 
    }) | null,
    error,
  };
}

// ============================================================
// POST/PUT/DELETE Queries
// ============================================================

export async function createRecensione(recensione: RecensioneInsert) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...recensione, user_id: userId })
    .select('*, strutture(nome), prenotazioni(ospite_nome, struttura_nome)')
    .single();

  return {
    data: data as (Recensione & { 
      strutture: { nome: string } | null;
      prenotazioni: { ospite_nome: string; struttura_nome: string } | null 
    }) | null,
    error,
  };
}

export async function updateRecensione(id: string, updates: RecensioneUpdate) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', userId)
    .select('*, strutture(nome), prenotazioni(ospite_nome, struttura_nome)')
    .single();

  return {
    data: data as (Recensione & { 
      strutture: { nome: string } | null;
      prenotazioni: { ospite_nome: string; struttura_nome: string } | null 
    }) | null,
    error,
  };
}

export async function deleteRecensione(id: string) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  return { error };
}
